/**
 * Upload Queue Tray — persistent bottom-right card showing every
 * in-flight, queued, completed, and failed upload. Survives route
 * changes because it's mounted at the App root. Collapses to a small
 * pill when minimized.
 *
 * Click any task's filename to jump to its source gallery.
 */
import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Upload, X, ChevronUp, ChevronDown, CheckCircle2, AlertCircle,
  Loader2, Pause, RotateCw, Trash2, ImageIcon, Film, Sparkles,
} from "lucide-react";
import { useUploadQueue, UPLOAD_STATUS } from "@/contexts/UploadQueueContext";

function fmtBytes(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function StatusIcon({ status }) {
  const cls = "shrink-0";
  switch (status) {
    case UPLOAD_STATUS.QUEUED:
      return <Pause size={14} className={`${cls} text-white/40`} />;
    case UPLOAD_STATUS.RUNNING:
    case UPLOAD_STATUS.RESUMING:
      return <Loader2 size={14} className={`${cls} text-gold animate-spin`} />;
    case UPLOAD_STATUS.COMPLETED:
      return <CheckCircle2 size={14} className={`${cls} text-green-400`} />;
    case UPLOAD_STATUS.FAILED:
      return <AlertCircle size={14} className={`${cls} text-red-400`} />;
    case UPLOAD_STATUS.CANCELLED:
      return <X size={14} className={`${cls} text-white/30`} />;
    default:
      return <Upload size={14} className={`${cls} text-white/40`} />;
  }
}

export default function UploadQueueTray() {
  const navigate = useNavigate();
  const { tasks, removeTask, retryTask, cancelTask, clearCompleted } = useUploadQueue();
  const [collapsed, setCollapsed] = useState(false);

  // Aggregate stats for the header summary.
  const summary = useMemo(() => {
    const running = tasks.filter((t) =>
      t.status === UPLOAD_STATUS.RUNNING || t.status === UPLOAD_STATUS.RESUMING,
    );
    const queued = tasks.filter((t) => t.status === UPLOAD_STATUS.QUEUED);
    const failed = tasks.filter((t) => t.status === UPLOAD_STATUS.FAILED);
    const cancelled = tasks.filter((t) => t.status === UPLOAD_STATUS.CANCELLED);
    const done = tasks.filter((t) => t.status === UPLOAD_STATUS.COMPLETED);
    const activeProgress = running[0]?.progress ?? 0;
    return { running, queued, failed, done, cancelled, activeProgress };
  }, [tasks]);

  if (tasks.length === 0) return null;

  const headerLabel = (() => {
    if (summary.running.length > 0) {
      return `Uploading ${summary.running[0].filename} — ${summary.activeProgress}%`;
    }
    if (summary.queued.length > 0) return `${summary.queued.length} queued`;
    if (summary.failed.length > 0) return `${summary.failed.length} failed`;
    return `${summary.done.length} done`;
  })();

  return (
    <AnimatePresence>
      <motion.div
        key="upload-tray"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 24 }}
        className="fixed bottom-4 right-4 z-[60] w-[340px] max-w-[calc(100vw-32px)] bg-luxury-charcoal/95 backdrop-blur-md border border-gold/30 shadow-2xl rounded-md overflow-hidden font-body"
        data-testid="upload-queue-tray"
      >
        {/* Header — a <div role="button"> not a <button>, because the
            "Clear" pill inside is itself a real <button> and HTML5
            forbids nested interactive elements. Keyboard-accessible
            via tabIndex + onKeyDown. */}
        <div
          role="button"
          tabIndex={0}
          className="w-full flex items-center justify-between gap-2 px-3 py-2.5 bg-luxury-black/80 hover:bg-luxury-black transition-colors border-b border-gold/20 cursor-pointer select-none"
          onClick={() => setCollapsed((c) => !c)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setCollapsed((c) => !c);
            }
          }}
          data-testid="upload-tray-toggle"
        >
          <div className="flex items-center gap-2 min-w-0">
            <Upload size={14} className="text-gold shrink-0" />
            <span className="text-gold text-xs truncate" data-testid="upload-tray-header-label">
              {headerLabel}
            </span>
          </div>
          <div className="flex items-center gap-1">
            {(summary.done.length > 0 || summary.cancelled?.length > 0) && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); clearCompleted(); }}
                className="text-white/40 hover:text-white text-[10px] px-1.5 py-0.5 hover:bg-white/10 rounded"
                title="Clear completed"
                data-testid="upload-tray-clear-done"
              >
                Clear
              </button>
            )}
            {collapsed ? <ChevronUp size={14} className="text-white/50" /> : <ChevronDown size={14} className="text-white/50" />}
          </div>
        </div>

        {/* Body */}
        <AnimatePresence initial={false}>
          {!collapsed && (
            <motion.div
              key="body"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="max-h-[60vh] overflow-y-auto"
              data-testid="upload-tray-body"
            >
              {tasks.map((t) => (
                <div
                  key={t.id}
                  className="px-3 py-2.5 border-b border-white/5 last:border-b-0 hover:bg-white/[0.02] transition-colors"
                  data-testid={`upload-task-${t.id}`}
                >
                  <div className="flex items-start gap-2">
                    <StatusIcon status={t.status} />
                    <button
                      type="button"
                      className="flex-1 min-w-0 text-left group"
                      onClick={() => navigate(`/admin/gallery/${t.galleryId}`)}
                      title="Open source gallery"
                      data-testid={`upload-task-open-${t.id}`}
                    >
                      <div className="flex items-center gap-1.5">
                        {t.kind === "chunk"
                          ? <Film size={10} className="text-white/30 shrink-0" />
                          : <ImageIcon size={10} className="text-white/30 shrink-0" />}
                        <p className="text-white text-[11px] truncate group-hover:text-gold transition-colors">
                          {t.filename}
                        </p>
                      </div>
                      <p className="text-white/30 text-[9px] mt-0.5">
                        {fmtBytes(t.size)}
                        {t.galleryTitle ? ` · ${t.galleryTitle}` : ""}
                      </p>
                    </button>
                    {/* Action buttons per status */}
                    {t.status === UPLOAD_STATUS.QUEUED && (
                      <button
                        type="button"
                        onClick={() => cancelTask(t.id)}
                        className="text-white/30 hover:text-red-400 transition-colors"
                        title="Cancel"
                        data-testid={`upload-task-cancel-${t.id}`}
                      >
                        <X size={12} />
                      </button>
                    )}
                    {t.status === UPLOAD_STATUS.FAILED && (
                      <>
                        <button
                          type="button"
                          onClick={() => retryTask(t.id)}
                          className="text-gold hover:text-gold-light transition-colors"
                          title="Retry"
                          data-testid={`upload-task-retry-${t.id}`}
                        >
                          <RotateCw size={12} />
                        </button>
                        <button
                          type="button"
                          onClick={() => removeTask(t.id)}
                          className="text-white/30 hover:text-red-400 transition-colors"
                          title="Remove"
                          data-testid={`upload-task-remove-${t.id}`}
                        >
                          <Trash2 size={12} />
                        </button>
                      </>
                    )}
                    {(t.status === UPLOAD_STATUS.COMPLETED || t.status === UPLOAD_STATUS.CANCELLED) && (
                      <button
                        type="button"
                        onClick={() => removeTask(t.id)}
                        className="text-white/30 hover:text-white transition-colors"
                        title="Dismiss"
                        data-testid={`upload-task-dismiss-${t.id}`}
                      >
                        <X size={12} />
                      </button>
                    )}
                  </div>

                  {/* Progress bar — visible while running AND after
                      completion (locked at 100%) so the user sees a
                      "filled bar = done" state instead of the bar
                      disappearing on success. */}
                  {(t.status === UPLOAD_STATUS.RUNNING
                    || t.status === UPLOAD_STATUS.RESUMING
                    || t.status === UPLOAD_STATUS.COMPLETED) && (
                    <div className="mt-1.5 h-1 bg-white/10 rounded-full overflow-hidden">
                      <motion.div
                        className={t.status === UPLOAD_STATUS.COMPLETED ? "h-full bg-green-400/70" : "h-full bg-gold"}
                        animate={{ width: `${t.status === UPLOAD_STATUS.COMPLETED ? 100 : t.progress}%` }}
                        transition={{ duration: 0.3 }}
                      />
                    </div>
                  )}
                  {/* Status label (resume hint, error) */}
                  {t.statusLabel && (
                    <p className="text-gold/70 text-[9px] mt-1" data-testid={`upload-task-status-${t.id}`}>
                      {t.statusLabel}
                    </p>
                  )}
                  {t.status === UPLOAD_STATUS.FAILED && t.error && (
                    <p className="text-red-400/80 text-[9px] mt-1" data-testid={`upload-task-error-${t.id}`}>
                      {t.error}
                    </p>
                  )}
                  {t.status === UPLOAD_STATUS.COMPLETED && t.kind === "batch" && (
                    <p className="text-green-400/80 text-[9px] mt-1">
                      {t.uploaded ?? t.files?.length} / {t.total ?? t.files?.length} uploaded
                    </p>
                  )}

                  {/* ARIA pre-processing preview — surfaces theme +
                      hashtags + headline while the upload is still
                      in flight. Renders only when the model returned
                      a useful prediction (confidence > 0.4). */}
                  {t.preview && t.preview.confidence > 0.4 && (
                    <div className="mt-2 pl-1 border-l-2 border-gold/40 ml-1" data-testid={`upload-task-preview-${t.id}`}>
                      <div className="flex items-center gap-1 mb-0.5">
                        <Sparkles size={9} className="text-gold shrink-0" />
                        <span className="text-gold/80 text-[9px] uppercase tracking-wide font-semibold">ARIA · {t.preview.theme}</span>
                      </div>
                      <p className="text-white/70 text-[10px] leading-tight mb-1">
                        {t.preview.headline}
                      </p>
                      {t.preview.hashtags?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-1">
                          {t.preview.hashtags.slice(0, 4).map((h) => (
                            <span key={h} className="text-[9px] text-gold/70 bg-gold/10 px-1 py-[1px]">
                              {h}
                            </span>
                          ))}
                        </div>
                      )}
                      {t.preview.suggestedActions?.length > 0 && (
                        <p className="text-white/30 text-[8px] italic">
                          Queued: {t.preview.suggestedActions.map((a) => a.replace(/_/g, " ")).join(" · ")}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </AnimatePresence>
  );
}
